import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkDeleteSuppliers,
  createSupplier,
  createSupplierResource,
  deleteSupplier,
  deleteSupplierResource,
  getSupplier,
  getSupplierCategories,
  listSuppliers,
  listSupplierResources,
  updateSupplier,
  updateSupplierCategories,
  type SupplierCategoryDef,
  type SupplierFilters,
  type SupplierInput,
  type SupplierResourceCategory,
} from '../lib/suppliersApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

const SUPPLIERS_KEY = ['suppliers'] as const;
const supplierResourcesKey = (id: string) => [...SUPPLIERS_KEY, 'resources', id] as const;

// --- Queries ---------------------------------------------------------------

export function useSuppliers(filters: SupplierFilters = {}) {
  // Global firm switcher (multi-company): threaded internally so pages don't
  // have to pass it. An explicit filters.companyId wins.
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: SupplierFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, merged],
    queryFn: () => listSuppliers(merged),
    placeholderData: (prev) => prev,
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, 'detail', id],
    queryFn: () => getSupplier(id as string),
    enabled: Boolean(id),
  });
}

// Settings-managed category nomenclature (code + label).
export function useSupplierCategories() {
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, 'categories'],
    queryFn: getSupplierCategories,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Mutations -------------------------------------------------------------

export function useUpdateSupplierCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categories: SupplierCategoryDef[]) => updateSupplierCategories(categories),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierInput) => createSupplier(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierInput) => updateSupplier(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}

export function useBulkDeleteSuppliers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteSuppliers(ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
  });
}

// --- Resources (CLE-249) ---------------------------------------------------

export function useSupplierResources(supplierId: string | undefined) {
  return useQuery({
    queryKey: supplierResourcesKey(supplierId ?? ''),
    queryFn: () => listSupplierResources(supplierId as string),
    enabled: Boolean(supplierId),
  });
}

export function useCreateSupplierResource(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      category: SupplierResourceCategory;
      s3Key: string;
      description?: string;
      fileType?: string;
      fileSize?: number;
    }) => createSupplierResource(supplierId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supplierResourcesKey(supplierId) });
    },
  });
}

export function useDeleteSupplierResource(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (resourceId: string) => deleteSupplierResource(resourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supplierResourcesKey(supplierId) });
    },
  });
}
